import { PermissionCombinator } from '../auth';

export type NotificationTarget = {
    /** Router commands, ready for `router.navigate`. */
    link: string[];
    /**
     * The guard on the target route. A caller must not offer the link without it: `Auth(...)` returns
     * `false` and the router cancels the navigation *silently*, so an ungated row reads as broken
     * rather than forbidden.
     */
    permission?: PermissionCombinator;
};

/** Contact routes are all behind this; spelled once so the entries below stay readable. */
const USERS_READ: PermissionCombinator = { and: ['users.read'] };

/** `settings/integrations` is a feature gate, not a role - see app-routing.module.ts. */
const INTEGRATIONS: PermissionCombinator = { or: ['facebook', 'whatsapp'] };
const MARKETING: PermissionCombinator = { or: ['marketing.manage'] };

/**
 * The record a notification is about, as a router link.
 *
 * The ids live in `Notification.data`, whose shape is decided by whichever backend call emitted the
 * row (`services/db.py`, `routers/user.py`, `user_import_worker.py`, `mail_account_health.py`,
 * `automation_engine/nodes.py`). Their key casing is inconsistent - `ticketId` next to `account_id`
 * next to a nested `branch.id` - and this table is deliberately the only place that has to know it.
 *
 * Returns `null` when the type has no target at all, and when the id the route needs is missing: an
 * `automation_message` from a run with no ticket or contact in context carries both ids as `null`.
 */
export function notificationTarget(type: string, data: any): NotificationTarget | null {
    const value = data ?? {};
    switch (type) {
        case 'new_message':
            return value.ticketId ? { link: ['/ticket-detail', String(value.ticketId)] } : null;

        case 'automation_message':
            // Whichever the run had. A flow that touched a ticket is best opened at the ticket; one
            // that only matched a contact has nowhere else to go.
            if (value.ticketId) return { link: ['/ticket-detail', String(value.ticketId)] };
            if (value.userId) return { link: ['/users', String(value.userId)], permission: USERS_READ };
            return null;

        // The surviving contact, not the source - the source id no longer resolves.
        case 'users_merged':
        case 'user_anonymized':
            return value.userId ? { link: ['/users', String(value.userId)], permission: USERS_READ } : null;

        // `services/newsletter_service.py` - the audit record of the send.
        case 'newsletter_queued':
        case 'newsletter_completed':
            return value.newsletterId
                ? { link: ['/newsletter', String(value.newsletterId)], permission: MARKETING }
                : null;

        case 'user_import_completed':
        case 'user_import_failed':
            return value.jobId
                ? { link: ['/users', 'import', 'jobs', String(value.jobId)], permission: USERS_READ }
                : null;

        // No per-account route exists for either of these, so the list page is the closest thing to
        // the record. Still worth linking: it is where the problem gets fixed.
        case 'mail_account_failing':
        case 'mail_account_recovered':
        case 'mail_processing_failed':
            return { link: ['/settings/email-accounts'] };

        case 'message_server_account_reassigned':
            return { link: ['/settings/locations'] };

        // Templates are studio-scoped now, so this no longer needs the account: the list shows every
        // account's verdict side by side, and the row updates live from this same payload.
        case 'template_status_update':
        case 'template_quality_update':
        case 'whatsapp_template_deploy_failed':
            return { link: ['/settings/integrations', 'templates'], permission: INTEGRATIONS };

        // The automations, not the templates: this notification exists because flows will now fail,
        // and the fix is to pick a different template in the flow or wait for Meta.
        case 'whatsapp_template_unusable':
            return { link: ['/settings/integrations', 'automation'], permission: INTEGRATIONS };

        case 'whatsapp_webhook_registration_failed':
            return { link: ['/settings/integrations'], permission: INTEGRATIONS };

        case 'automation_send_backlog':
            return { link: ['/settings/integrations', 'automation'], permission: INTEGRATIONS };

        /**
         * Deliberately unlinked:
         *
         * - `user_hard_deleted` - the contact is gone, so `/users/:id` would reject in its resolver
         *   and cancel the navigation without a word.
         * - `office365_connected` carries only a username string, `text` only a title and body.
         *   Neither identifies a record.
         */
        default:
            return null;
    }
}
